import merge from 'lodash/merge';
import {
    ILogger,
    LoggerCategoryEnum,
    LoggerLevelEnum,
    ILogQueue,
    ICreateLoggerCore,
    ILog,
    ILogQueueLog,
    TransportsType,
    LogDataType
} from './LoggerCore.types';
import { getInitialLogQueue, logLevels } from './LoggerCore.utils';

// Singleton instance
let loggerInstance: LoggerCore;

/**
 * The Logger Core is the base `class` which the browser and server logger extend.
 *
 * You probably want to use the browser or server specific Logger.
 */
class LoggerCore implements ILogger {
    private transports: {
        [LoggerCategoryEnum.default]: TransportsType;
        [LoggerCategoryEnum.analytics]: TransportsType;
        [LoggerCategoryEnum.diagnostic]: TransportsType;
        [LoggerCategoryEnum.api]: TransportsType;
        [LoggerCategoryEnum.ui]: TransportsType;
    };

    // log store/queue
    private logQueue: ILogQueue = getInitialLogQueue();

    // An array of current events being posted
    private logCategoriesPosting: LoggerCategoryEnum[] = [];

    // an array of events that are queued to be posted at a later time.
    private logCategoriesToBePostedLater: LoggerCategoryEnum[] = [];

    // should the class autoPost the logs
    private autoPost: boolean;

    // autoPost interval
    private autoPostFrequency: number;

    private logLevel: LoggerLevelEnum;

    private isConsoleDisabled: boolean = false;

    private logLevelInCookie: LoggerLevelEnum = undefined;

    private enableMasking: boolean = true;

    private maskFields: string[] = [];

    public consoleLevelValue: LoggerLevelEnum;

    /**
     * A setter utilised by the parent Class.
     *
     * @memberof LoggerCore
     */
    public set consoleLevel(value: LoggerLevelEnum) {
        this.consoleLevelValue = value;
    }

    // data included in every log
    private defaultLogData: {
        platform: string;
        name: string;
        environment: string;
        parentAppStack: string[];
        version: string;
    } = undefined;

    // interval timer for auto post
    private timer: number = undefined;

    // timeouts for failed request that are re-queued.
    private requeueTimers: {
        [LoggerCategoryEnum.default]?: number;
        [LoggerCategoryEnum.analytics]?: number;
        [LoggerCategoryEnum.diagnostic]?: number;
        [LoggerCategoryEnum.api]?: number;
        [LoggerCategoryEnum.ui]?: number;
    } = {};

    /**
     * Configure the `Logger` and return a Logger instance with `log`, `info`, `debug`, `error` and `warn` methods.
     *
     * @param config Configuration data
     * @param config.transports
     * @param config.autoPost Should the logs be automatically posted. If false you will need to handle posting by calling `instance.postLogs()`.
     * @param config.autoPostFrequencyThe frequency in milliseconds which logs will be automatically posted.
     * @param config.appName The name of the application.
     * @param config.appVersion The applications version number/hash/identifier.
     * @param config.logLevel Setting the level at which you wish to collect logs. The levels in ascending order are: `[log, info, debug, warn, error]` so if you set for example a logLevel of warn, only warn and error logs will be captured by the queue.
     * @param config.consoleLevel Similar to log level but only applies to logs transported to a console transport.
     * @param config.platform the platform/environment, eg. nab-x, nab-one, ib...
     * @param config.environment The environment the application is running within. eg, dev, prod...
     * @param config.enableMasking Should sensitive fields in data objects be masked. If true, field values for fields configured in config.maskFields will be masked.
     * @param config.maskFields All the fields (keys) in data objects whose values should be masked.

     * @returns {ILoggerFns} The log interface. ie. `log`, `info`, `debug`, `error`, `warn` methods.
     */
    constructor(settings: ICreateLoggerCore) {
        const {
            transports,
            autoPost = true,
            autoPostFrequency = 30000,
            appName,
            appVersion,
            logLevel,
            parentAppStack,
            platform,
            environment,
            enableMasking = true,
            maskFields = []
        } = settings;

        if (!loggerInstance) {
            loggerInstance = this;
        }

        loggerInstance.transports = transports;
        loggerInstance.autoPost = autoPost;
        loggerInstance.autoPostFrequency = autoPostFrequency;
        loggerInstance.enableMasking = enableMasking;
        loggerInstance.maskFields = maskFields;
        loggerInstance.defaultLogData = {
            platform,
            environment,
            parentAppStack,
            name: appName,
            version: appVersion
        };
        loggerInstance.logLevel = logLevel;
        loggerInstance.autoPoster();

        return loggerInstance;
    }

    /**
     * Queue a log with the level of `log`.
     * @example Basic usage
     * ```js
     * logger.log(LoggerCategoryEnum.analytics, 'Some Message', {data: 'value'});
     * ```
     * @example You can ommit the first param log category, data will be logged to the default category.
     * ```js
     * logger.log('Some message', {data: 'value'}, {data1: 'value'}, {data2: 'value'});
     * ```
     *
     * @param logCategoryOrMessage A log category or a message to log
     * @param messageOrData A message to log or data to save to the log
     * @param data Data to save to the log, you can pass many data object in as you wish.
     */
    public log(
        logCategoryOrMessage: LoggerCategoryEnum | LoggerCategoryEnum[] | string,
        messageOrData?: string | LogDataType,
        ...data: LogDataType[]
    ) {
        this.logInterface(LoggerLevelEnum.log, logCategoryOrMessage, messageOrData, ...data);
    }

    /**
     * Queue a log with the level of `info`.
     * @example Basic usage
     * ```js
     * logger.info(LoggerCategoryEnum.analytics, 'Some Message', {data: 'value'});
     * ```
     * @example You can ommit the first param log category, data will be logged to the default category.
     * ```js
     * logger.info('Some message', {data: 'value'}, {data1: 'value'}, {data2: 'value'});
     * ```
     *
     * @param logCategoryOrMessage A log category or a message to log
     * @param messageOrData A message to log or data to save to the log
     * @param data Data to save to the log, you can pass many data object in as you wish.
     */
    public info(
        logCategoryOrMessage: LoggerCategoryEnum | LoggerCategoryEnum[] | string,
        messageOrData?: string | LogDataType,
        ...data: LogDataType[]
    ) {
        this.logInterface(LoggerLevelEnum.info, logCategoryOrMessage, messageOrData, ...data);
    }

    /**
     * Queue a log with the level of `debug`.
     * @example Basic usage
     * ```js
     * logger.debug(LoggerCategoryEnum.analytics, 'Some Message', {data: 'value'});
     * ```
     * @example You can ommit the first param log category, data will be logged to the default category.
     * ```js
     * logger.debug('Some message', {data: 'value'}, {data1: 'value'}, {data2: 'value'});
     * ```
     *
     * @param logCategoryOrMessage A log category or a message to log
     * @param messageOrData A message to log or data to save to the log
     * @param data Data to save to the log, you can pass many data object in as you wish.
     */
    public debug(
        logCategoryOrMessage: LoggerCategoryEnum | LoggerCategoryEnum[] | string,
        messageOrData?: string | LogDataType,
        ...data: LogDataType[]
    ) {
        this.logInterface(LoggerLevelEnum.debug, logCategoryOrMessage, messageOrData, ...data);
    }

    /**
     * Queue a log with the level of `warn`.
     * @example Basic usage
     * ```js
     * logger.warn(LoggerCategoryEnum.analytics, 'Some Message', {data: 'value'});
     * ```
     * @example You can ommit the first param log category, data will be logged to the default category.
     * ```js
     * logger.warn('Some message', {data: 'value'}, {data1: 'value'}, {data2: 'value'});
     * ```
     *
     * @param logCategoryOrMessage A log category or a message to log
     * @param messageOrData A message to log or data to save to the log
     * @param data Data to save to the log, you can pass many data object in as you wish.
     */
    public warn(
        logCategoryOrMessage: LoggerCategoryEnum | LoggerCategoryEnum[] | string,
        messageOrData?: string | LogDataType,
        ...data: LogDataType[]
    ) {
        this.logInterface(LoggerLevelEnum.warn, logCategoryOrMessage, messageOrData, ...data);
    }

    /**
     * Queue a log with the level of `error`.
     * @example Basic usage
     * ```js
     * logger.error(LoggerCategoryEnum.analytics, 'Some Message', {data: 'value'});
     * ```
     * @example You can ommit the first param log category, data will be logged to the default category.
     * ```js
     * logger.error('Some message', {data: 'value'}, {data1: 'value'}, {data2: 'value'});
     * ```
     *
     * @param logCategoryOrMessage A log category or a message to log
     * @param messageOrData A message to log or data to save to the log
     * @param data Data to save to the log, you can pass many data object in as you wish.
     */
    public error(
        logCategoryOrMessage: LoggerCategoryEnum | LoggerCategoryEnum[] | string,
        messageOrData?: string | LogDataType,
        ...data: LogDataType[]
    ) {
        this.logInterface(LoggerLevelEnum.error, logCategoryOrMessage, messageOrData, ...data);
    }

    /**
     * Destroy the `Logger` instance.
     */
    public destroy() {
        // clear the autoPost interval
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = undefined;
        }
        // clear any post that have been requeued due to errors.
        Object.keys(this.requeueTimers).forEach((timerKey) => {
            const requeueTimer = this.requeueTimers[timerKey];
            /* istanbul ignore next */
            if (requeueTimer) {
                clearTimeout(requeueTimer);
                delete this.requeueTimers[timerKey];
            }
        });

        // reset instance properties
        this.logQueue = getInitialLogQueue();
        this.defaultLogData = undefined;
        this.logCategoriesPosting = [];
        this.logCategoriesToBePostedLater = [];
    }

    private autoPoster() {
        if (this.autoPost && this.autoPostFrequency > 0 && !this.timer) {
            this.timer = <any>setInterval(() => this.postLogs(), this.autoPostFrequency);
        }
    }

    private logToQueue(level: LoggerLevelEnum, logCategory: LoggerCategoryEnum | LoggerCategoryEnum[], message: string, data?: LogDataType) {
        const logCategoryKeys = Array.isArray(logCategory) ? logCategory : [logCategory];
        logCategoryKeys.forEach((logCategoryKey) => {
            this.logQueue[logCategoryKey].push({
                level,
                message,
                category: logCategoryKey,
                data,
                timestamp: Date.now()
            });
        });
    }

    private logInterface = (
        level: LoggerLevelEnum,
        logCategoryOrMessage: LoggerCategoryEnum | LoggerCategoryEnum[] | string,
        messageOrData?: string | LogDataType,
        ...data: LogDataType[]
    ) => {
        const islogCategoryGiven = Array.isArray(logCategoryOrMessage) || LoggerCategoryEnum[logCategoryOrMessage] !== undefined;

        const logCategory: LoggerCategoryEnum | LoggerCategoryEnum[] = islogCategoryGiven
            ? (logCategoryOrMessage as LoggerCategoryEnum | LoggerCategoryEnum[])
            : LoggerCategoryEnum.default;
        const message: string = islogCategoryGiven ? (messageOrData as string) : (logCategoryOrMessage as string);

        let mergedData;
        if (typeof messageOrData === 'string') {
            mergedData = data.length > 0 ? merge({}, ...data) : undefined;
        } else {
            mergedData = messageOrData ? merge({}, messageOrData, ...data) : undefined;
        }

        if (this.enableMasking === true) {
            const maskedData = this.getMaskedObject(mergedData);
            this.logToQueue(level, logCategory, message, maskedData);
        } else {
            this.logToQueue(level, logCategory, message, mergedData);
        }
    };

    private managePostQueue(logCategory: LoggerCategoryEnum) {
        // if there's an existing post request do not post logs, but cache the logCategory to be posted later.
        if (this.logQueue[logCategory].length > 0) {
            if (this.logCategoriesPosting.indexOf(logCategory) === -1) {
                this.post(logCategory);
                this.logCategoriesPosting.push(logCategory);
            } else {
                this.logCategoriesToBePostedLater.push(logCategory);
            }
        }
    }

    /**
     * Function to mask sensitive fields in an object.
     * Creates and returns a clone of original object with sensitive fields masked.
     * Does not handle circular references.
     * @param objectToBeMasked - Object having sensitive fields to be masked
     * @returns cloned object with sensitive fields masked.
     */
    private getMaskedObject(objectToBeMasked: object): object {
        const MASK = '*****';

        if (objectToBeMasked === null || typeof objectToBeMasked !== 'object') {
            return objectToBeMasked;
        }

        // Return original object if mask fields are not set.
        if (!this.maskFields?.length) {
            return objectToBeMasked;
        }

        let maskedObject = null;
        if (Array.isArray(objectToBeMasked)) {
            maskedObject = [];
            objectToBeMasked.forEach((element, index) => {
                if (typeof element === 'object') {
                    maskedObject[index] = this.getMaskedObject(element);
                } else {
                    maskedObject[index] = element;
                }
            });
        } else {
            const initialValue: { [key: string]: any } = {};
            maskedObject = Object.entries(objectToBeMasked).reduce((accumulator, currentValue) => {
                // Check and mask current object field value if required
                const matchedMaskField = this.maskFields.find((element: string) => {
                    if (currentValue[0] === element) {
                        accumulator[currentValue[0]] = MASK;

                        return true;
                    }
                    return false;
                });

                // Even if current object field is not to be masked,
                // we need to check if the field value is an object
                // and recursively check if it has any fields to be
                // masked.
                if (matchedMaskField === undefined) {
                    if (typeof currentValue[1] === 'object') {
                        accumulator[currentValue[0]] = this.getMaskedObject(currentValue[1]);
                    } else {
                        // eslint-disable-next-line prefer-destructuring
                        accumulator[currentValue[0]] = currentValue[1];
                    }
                }

                return accumulator;
            }, initialValue);
        }

        return maskedObject;
    }

    /**
     * Post the log queue to the specified transports. Transports are configured when calling `create`.
     * @param logCategory Specify which transport you wish to post to. If no transport is specified all transports will be sent.
     */
    public postLogs(logCategory?: LoggerCategoryEnum, isConsoleDisabled?: boolean, logLevelInCookie?: LoggerLevelEnum) {
        this.isConsoleDisabled = isConsoleDisabled;
        this.logLevelInCookie = logLevelInCookie;
        if (logCategory) {
            this.managePostQueue(logCategory);
        } else {
            Object.keys(this.logQueue).forEach((logCategoryinQueue) => {
                this.managePostQueue(LoggerCategoryEnum[logCategoryinQueue]);
            });
        }
    }

    private post(logCategory: LoggerCategoryEnum) {
        // clone the log array
        const logQueue = this.logQueue[logCategory];
        const logsClone = logQueue.slice(0);
        // empty the log array. If the transport request fails the data will be added
        // back to the start of the array, allowing any further logs to be captured
        // and queued whilst the request returns
        this.logQueue[logCategory] = [];

        const transports = this.transports[logCategory];
        const transportsArray = Array.isArray(transports) ? transports : [transports];

        transportsArray.forEach((transport) => {
            const { isBrowserConsoleTransport } = transport.prototype;

            const configuredLogLevel = isBrowserConsoleTransport ? this.consoleLevelValue : this.logLevel;

            const postableLevel: LoggerLevelEnum = LoggerLevelEnum[this.logLevelInCookie] || configuredLogLevel;

            // parse out any logs below this.logLevel
            // if we're sending to a console transport prefer the logLevelInCookie param
            const postableLogs: ILogQueueLog[] =
                !postableLevel || (isBrowserConsoleTransport && this.isConsoleDisabled)
                    ? []
                    : logsClone.filter((log) => logLevels.indexOf(log.level) >= logLevels.indexOf(postableLevel));

            // add the default data into the logs
            const updatedLogs = postableLogs.map((log) => ({ ...log, ...this.defaultLogData }));

            return transport(updatedLogs as ILog[])
                .then(() => {
                    const logCategoryQueueIndex = this.logCategoriesPosting.indexOf(logCategory);
                    if (logCategoryQueueIndex !== -1) {
                        this.logCategoriesPosting.splice(logCategoryQueueIndex, 1);
                    }
                    const postQueueIndex = this.logCategoriesToBePostedLater.indexOf(logCategory);
                    if (postQueueIndex !== -1) {
                        this.post(logCategory);
                        this.logCategoriesToBePostedLater.splice(postQueueIndex, 1);
                    }
                })
                .catch((error) => {
                    // if an error was found insert the log data back into the queue
                    this.logQueue[logCategory].unshift(...logsClone);
                    // re queue the same log event type
                    /* istanbul ignore next */
                    if (!this.autoPost) {
                        this.requeueTimers[logCategory] = window.setTimeout(() => this.post(logCategory), 10000);
                    }
                });
        });
    }
}

export { LoggerCore };
